import { lazy, Suspense, useEffect, useState } from "react";

import { HttpStatusCode } from "axios";
import { Button, Input, useDisclosure, useToast } from "@chakra-ui/react";
import { useNavigate } from "react-router-dom";

import AddIcon from "@/assets/AddIcon";
import Loader from "@/components/Layouts/Loader";
import ModalDelete from "@/components/Modals/ModalDelete";
import { COLORS, DEFAULT_PAGE_NUMBER, DEFAULT_PAGE_SIZE, DURATION_TOAST } from "@/common/constants";
import { deleteRole, getListRole } from "@/services/roleService";

const TableRole = lazy(() => import('@/components/Tables/TableRole'));

const RoleList = () => {
    const navigate = useNavigate();
    const toast = useToast();
    const { isOpen, onOpen, onClose } = useDisclosure();

    const [currentPageNumber, setCurrentPageNumber] = useState(DEFAULT_PAGE_NUMBER);
    const [currentPageSize, setCurrentPageSize] = useState(DEFAULT_PAGE_SIZE);
    const [dataList, setDataList] = useState([]);
    const [hasNextPage, setHasNextPage] = useState(false);
    const [idToDelete, setIdToDelete] = useState('');
    const [isLoading, setIsLoading] = useState(true);
    const [searchValue, setSearchValue] = useState('');
    const [totalPageNumber, setTotalPageNumber] = useState(1);

    const getData = async () => {
        setIsLoading(true);
        const param = { search: searchValue, pageNumber: currentPageNumber, pageSize: currentPageSize };

        try {
            const response = await getListRole(param) as any;
            if (response.status === HttpStatusCode.Ok) {
                setDataList(response.data);
                setTotalPageNumber(response.totalPage);
                setHasNextPage(response.hasNextPage);
            } else {
                showToast(response);
            }
        } catch (error) {
            showToast({ code: 'Error', message: 'Failed to fetch data' });
        } finally {
            setIsLoading(false);
        }
    };

    const handleDelete = (id: string) => {
        setIdToDelete(id);
        onOpen();
    };

    const handleConfirmDelete = async () => {
        try {
            const response = await deleteRole(idToDelete) as any;
            if (response.status === HttpStatusCode.Ok) {
                await getData();
                toast({
                    title: 'Role deleted.',
                    description: "We've deleted the role.",
                    status: 'success',
                    duration: DURATION_TOAST,
                    isClosable: true,
                });
            } else {
                showToast({ code: 'Delete Failed', message: "We failed to delete the role." });
            }
        } catch (error) {
            showToast({ code: 'Delete Error', message: "An error occurred while deleting." });
        } finally {
            onClose();
        }
    };

    const showToast = (error: any) => {
        toast({
            title: error?.status,
            description: error?.message,
            status: 'error',
            duration: DURATION_TOAST,
            isClosable: true,
        });
    };

    const handleChange = (setter: React.Dispatch<React.SetStateAction<any>>) => (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
        setter(e.target.value);
    };

    const handleSearch = () => {
        setCurrentPageNumber(1);
        setTimeout(() => {
            getData();
        }, 300);
    };

    useEffect(() => {
        getData();
    }, [currentPageSize, currentPageNumber]);

    return (
        <>
            <ModalDelete title="Delete it?" isOpen={isOpen} onClose={onClose} handleConfirmDelete={handleConfirmDelete} />
            <div className="table-container">
                <div className="table-filter-action-container">
                    <div className="table-filter-container">
                        <div className="table-filter-input filter-input-width-9">
                            <Input
                                placeholder='Search'
                                onChange={handleChange(setSearchValue)}
                            />
                        </div>
                        <div>
                            <Button
                                className='button'
                                onClick={handleSearch}
                            >
                                Search
                            </Button>
                        </div>
                        <div className="table-filter-separator"/>
                        <Button
                            leftIcon={<AddIcon />}
                            bgColor={COLORS.BUTTON.BACKGROUND}
                            color={COLORS.BUTTON.FOREGROUND}
                            borderRadius={10}
                            variant='solid'
                            className='button table-add-button'
                            onClick={() => navigate('/user-management/role/add')}
                        >
                            Add Role
                        </Button>
                    </div>
                </div>
                <Suspense fallback={<Loader />}>
                    <TableRole
                        dataList={dataList}
                        isLoading={isLoading}
                        handleEdit={(id: string) => navigate('/user-management/role/edit', { state: { id } })}
                        handleDelete={handleDelete}
                        currentPageSize={currentPageSize}
                        handleChangePageSize={setCurrentPageSize}
                        handleChangePageNumber={setCurrentPageNumber}
                        currentPageNumber={currentPageNumber}
                        totalPageNumber={totalPageNumber}
                        hasNextPage={hasNextPage}
                    />
                </Suspense>
            </div>
        </>
    )
}

export default RoleList;
